import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSSEStream } from '../../hooks/useSSEStream';
import useChatSessions from './useChatSessions';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import { MESSAGE_ROLES, STEP_STATUS, EVENT_TYPES, ERROR_TYPES, INTERACTIVE_SERVICE_BASE_URL } from '../constants';

const createMessageId = () => `${MESSAGE_ROLES.ASSISTANT}-${Date.now()}`;

const SSE_COMPLETION_EVENTS = [
    EVENT_TYPES.COMPLETED,
    EVENT_TYPES.ERROR,
    EVENT_TYPES.USER_ERROR,
    EVENT_TYPES.CANCELLED,
    EVENT_TYPES.FINISH,
];

const createAssistantMessage = messageId => ({
    id: messageId,
    role: MESSAGE_ROLES.ASSISTANT,
    content: [], // Content is an array of blocks: [{type: 'markdown', data: '...'}, {type: 'code', data: {...}}, ...]
    isThinking: true,
    isStreaming: false,
    createdAt: new Date(),
    steps: [],
    ephemeralUpdates: [],
});

const updateMessageSteps = (existingSteps, newStep) => {
    const stepIndex = existingSteps.findIndex(s => s.step_id === newStep.step_id);
    const updatedSteps =
        stepIndex >= 0
            ? existingSteps.map((step, idx) => (idx === stepIndex ? { ...step, ...newStep } : step))
            : [...existingSteps, newStep];

    return {
        steps: updatedSteps,
        isThinking: updatedSteps.some(s => s.status === STEP_STATUS.RUNNING),
    };
};

const useChat = ({ runPath = 'runs', sessionType, onFinish, loadSessionsOnMount = true } = {}) => {
    const api = INTERACTIVE_SERVICE_BASE_URL;
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState(null);
    const [currentRunId, setCurrentRunId] = useState(null);
    const [stopError, setStopError] = useState(null);
    const streamingMessageId = useRef(null);
    const onFinishRef = useRef(onFinish);
    const inputRef = useRef('');

    useEffect(() => {
        onFinishRef.current = onFinish;
    }, [onFinish]);

    useEffect(() => {
        inputRef.current = input;
    }, [input]);

    const onSessionChange = useCallback((sessionId, shouldClearMessages, loadedMessages = null, sessionError = null) => {
        if (shouldClearMessages) setMessages([]);
        else if (loadedMessages) setMessages(loadedMessages);

        setError(sessionError);
        setStopError(null);
    }, []);

    const {
        currentSessionId,
        setCurrentSessionId,
        sessions,
        isLoadingSessions,
        loadingSessionId,
        fetchSessions,
        upsertSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    } = useChatSessions({ api, sessionType, onSessionChange, loadSessionsOnMount });

    const resetChatState = useCallback(() => {
        setIsLoading(false);
        setCurrentRunId(null);
        setStopError(null);
        streamingMessageId.current = null;
    }, []);

    const clearCurrentSession = useCallback(() => {
        setCurrentSessionId(null);
        setMessages([]);
        setError(null);
    }, [setCurrentSessionId]);

    const handleError = useCallback((errorMessage, isUserError = false) => {
        const assistantMessageId = streamingMessageId.current;
        resetChatState();
        setError(null);

        if (!isUserError) {
            setError(errorMessage);
            return;
        }

        const textContent = [{ type: 'error', data: errorMessage }];
        if (!assistantMessageId) {
            setMessages(prev => [...prev, { ...createAssistantMessage(createMessageId()), isThinking: false, content: textContent }]);
        } else {
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === assistantMessageId
                        ? { ...msg, isThinking: false, isStreaming: false, steps: [], content: [...(msg.content || []), ...textContent] }
                        : msg
                )
            );
        }
    }, [resetChatState]);

    // Lifecycle event handlers
    const handleStarted = useCallback(() => {
        const messageId = createMessageId();
        streamingMessageId.current = messageId;
        setMessages(prev => [...prev, createAssistantMessage(messageId)]);
    }, []);

    const handleCompleted = useCallback(() => {
        setMessages(prev => {
            const updatedMessages = prev.map(msg => {
                if (msg.id !== streamingMessageId.current) return msg;

                const completedSteps = (msg.steps || []).map(step =>
                    step.status !== STEP_STATUS.FAILED ? { ...step, status: STEP_STATUS.COMPLETED } : step
                );
                return { ...msg, isThinking: false, isLoading: false, isStreaming: false, steps: completedSteps };
            });

            const finishedMessage = updatedMessages.find(m => m.id === streamingMessageId.current);
            if (finishedMessage) {
                onFinishRef.current?.(finishedMessage);
            }

            return updatedMessages;
        });
        resetChatState();
    }, [resetChatState]);

    const handleCancelled = useCallback(message => {
        const assistantMessageId = streamingMessageId.current;
        resetChatState();

        if (!assistantMessageId) return;

        const displayedMessage = message?.message ?? '**Response was interrupted**';
        setMessages(prev =>
            prev.map(msg => {
                if (msg.id !== assistantMessageId) return msg;
                const stoppedSteps = (msg.steps || []).map(step =>
                    step.status === STEP_STATUS.RUNNING ? { ...step, status: STEP_STATUS.FAILED } : step
                );
                const stoppedEphemerals = (msg.ephemeralUpdates || []).map(update =>
                    update.isStreaming ? { ...update, isStreaming: false } : update
                );
                return {
                    ...msg,
                    isThinking: false,
                    isStreaming: false,
                    steps: stoppedSteps,
                    ephemeralUpdates: stoppedEphemerals,
                    content: Array.isArray(msg.content) && msg.content.length
                        ? [...msg.content, { type: 'markdown', data: `\n\n${displayedMessage}` }]
                        : [{ type: 'markdown', data: displayedMessage }],
                };
            })
        );
    }, [resetChatState]);

    // Content event handlers
    const handleToken = useCallback(message => {
        const content = message.content || message.token || '';
        if (content && streamingMessageId.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== streamingMessageId.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Get the last content block
                    const lastBlock = updatedContent[updatedContent.length - 1];

                    // If last block is markdown, append to it; otherwise create new markdown block
                    if (lastBlock && lastBlock.type === 'markdown') {
                        lastBlock.data += content;
                    } else {
                        updatedContent.push({ type: 'markdown', data: content });
                    }

                    return { ...msg, content: updatedContent, isThinking: false, isStreaming: true };
                })
            );
        }
    }, []);

    const handleStepUpdate = useCallback(message => {
        const { step } = message;
        if (step && streamingMessageId.current) {
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === streamingMessageId.current
                        ? { ...msg, ...updateMessageSteps(msg.steps || [], step) }
                        : msg
                )
            );
        }
    }, []);

    const handleArtifact = useCallback(message => {
        const { artifact } = message;
        if (artifact && streamingMessageId.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== streamingMessageId.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Add artifact as a content block
                    updatedContent.push(artifact);

                    return { ...msg, content: updatedContent };
                })
            );
        }
    }, []);

    const upsertEphemeralUpdate = useCallback((updateId, mutator) => {
        if (!streamingMessageId.current) return;

        setMessages(prev =>
            prev.map(msg => {
                if (msg.id !== streamingMessageId.current) return msg;

                const existing = msg.ephemeralUpdates || [];
                const idx = existing.findIndex(u => u.id === updateId);
                const current = idx >= 0 ? existing[idx] : { id: updateId, text: '', isStreaming: true };
                const next = mutator(current);
                const updated = idx >= 0
                    ? existing.map((u, i) => (i === idx ? next : u))
                    : [...existing, next];
                return { ...msg, ephemeralUpdates: updated, isThinking: false };
            })
        );
    }, []);

    const handleEphemeralUpdateStart = useCallback(message => {
        upsertEphemeralUpdate(message.id, current => ({
            ...current,
            title: message.title || current.title || '',
            isStreaming: true,
        }));
    }, [upsertEphemeralUpdate]);

    const handleEphemeralUpdateDelta = useCallback(message => {
        upsertEphemeralUpdate(message.id, current => ({
            ...current,
            text: (current.text || '') + (message.delta || ''),
            isStreaming: true,
        }));
    }, [upsertEphemeralUpdate]);

    const handleEphemeralUpdateEnd = useCallback(message => {
        upsertEphemeralUpdate(message.id, current => ({ ...current, isStreaming: false }));
    }, [upsertEphemeralUpdate]);

    const handleResponseComplete = useCallback(() => {
        if (streamingMessageId.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== streamingMessageId.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Add line break to last markdown block if it exists
                    const lastBlock = updatedContent[updatedContent.length - 1];
                    if (lastBlock && lastBlock.type === 'markdown') {
                        lastBlock.data += '\n\n';
                    }

                    return { ...msg, isStreaming: false, content: updatedContent };
                })
            );
        }
    }, []);

    const handleFinish = useCallback(
        message => {
            const reason = message.finishReason;
            if (reason === 'cancelled') return handleCancelled(message);
            if (reason === 'error') return handleError(message.errorText || 'An error occurred', true);
            return handleCompleted();
        },
        [handleCompleted, handleCancelled, handleError]
    );

    const handleToolInputStart = useCallback(
        message => {
            const step = {
                step_id: message.toolCallId,
                name: message.toolName,
                title: message.toolName,
                status: STEP_STATUS.RUNNING,
            };
            handleStepUpdate({ step });
        },
        [handleStepUpdate]
    );

    const handleToolOutputAvailable = useCallback(
        message => {
            const step = {
                step_id: message.toolCallId,
                status: STEP_STATUS.COMPLETED,
            };
            handleStepUpdate({ step });
        },
        [handleStepUpdate]
    );

    const handleDataEvent = useCallback(
        message => {
            const dataType = message.type.slice(EVENT_TYPES.DATA_PREFIX.length);
            if (dataType === 'artifact' && message.data) {
                const artifactData = message.data;
                const artifact = {
                    type: artifactData.artifact_type || dataType,
                    data: artifactData.data,
                    title: artifactData.title,
                    insights: artifactData.insights,
                    is_final_analysis: artifactData.is_final_analysis,
                };
                handleArtifact({ artifact });
            }
        },
        [handleArtifact]
    );

    // Lifecycle events run regardless of streaming state (start/finish/error/etc).
    const lifecycleHandlers = useMemo(() => ({
        [EVENT_TYPES.STARTED]: () => handleStarted(),
        [EVENT_TYPES.COMPLETED]: () => handleCompleted(),
        [EVENT_TYPES.CANCELLED]: message => handleCancelled(message),
        [EVENT_TYPES.RESPONSE_COMPLETE]: () => handleResponseComplete(),
        [EVENT_TYPES.SESSION_TITLE_UPDATED]: message => upsertSession(message.session_id, message.title),
        [EVENT_TYPES.START]: () => handleStarted(),
        [EVENT_TYPES.FINISH]: message => handleFinish(message),
        [EVENT_TYPES.STREAM_ERROR]: message => handleError(message.errorText || 'An error occurred', true),
    }), [handleStarted, handleCompleted, handleCancelled, handleResponseComplete, upsertSession, handleFinish, handleError]);

    // Content events only run while an assistant message is streaming. Unknown
    // event types (reasoning-*, step-start/finish, text-start) fall through silently.
    const contentHandlers = useMemo(() => ({
        [EVENT_TYPES.TOKEN]: message => handleToken(message),
        [EVENT_TYPES.TOKEN_BATCH]: message => handleToken(message),
        [EVENT_TYPES.STEP_UPDATE]: message => handleStepUpdate(message),
        [EVENT_TYPES.ARTIFACT]: message => handleArtifact(message),
        [EVENT_TYPES.TEXT_DELTA]: message => handleToken({ content: message.delta }),
        [EVENT_TYPES.TEXT_END]: () => handleResponseComplete(),
        [EVENT_TYPES.TOOL_INPUT_START]: message => handleToolInputStart(message),
        [EVENT_TYPES.TOOL_OUTPUT_AVAILABLE]: message => handleToolOutputAvailable(message),
        [EVENT_TYPES.EPHEMERAL_UPDATE_START]: message => handleEphemeralUpdateStart(message),
        [EVENT_TYPES.EPHEMERAL_UPDATE_DELTA]: message => handleEphemeralUpdateDelta(message),
        [EVENT_TYPES.EPHEMERAL_UPDATE_END]: message => handleEphemeralUpdateEnd(message),
    }), [handleToken, handleStepUpdate, handleArtifact, handleResponseComplete, handleToolInputStart, handleToolOutputAvailable, handleEphemeralUpdateStart, handleEphemeralUpdateDelta, handleEphemeralUpdateEnd]);

    const handleSSEMessage = useCallback(
        message => {
            const { type } = message;

            const lifecycleHandler = lifecycleHandlers[type];
            if (lifecycleHandler) return lifecycleHandler(message);

            if (Object.values(ERROR_TYPES).includes(type)) {
                return handleError(message.message || message.error || 'An error occurred', type === ERROR_TYPES.USER_ERROR);
            }

            if (!streamingMessageId.current) return;

            const contentHandler = contentHandlers[type];
            if (contentHandler) return contentHandler(message);

            if (type?.startsWith(EVENT_TYPES.DATA_PREFIX)) return handleDataEvent(message);
        },
        [lifecycleHandlers, contentHandlers, handleError, handleDataEvent]
    );

    const { isConnected: sseConnected, error: sseError } = useSSEStream(INTERACTIVE_SERVICE_BASE_URL, currentRunId, handleSSEMessage, SSE_COMPLETION_EVENTS);

    useEffect(() => {
        if (sseError) handleError(sseError);
    }, [sseError, handleError]);

    const handleSubmit = useCallback(
        async (e, options = {}) => {
            e?.preventDefault();

            const messageContent = options?.content || inputRef.current;

            if (!messageContent.trim() || isLoading) return;

            setError(null);
            setStopError(null);
            setIsLoading(true);

            const userMessage = {
                id: `${MESSAGE_ROLES.USER}-${Date.now()}`,
                role: MESSAGE_ROLES.USER,
                content: [{ type: 'markdown', data: messageContent }],
                createdAt: new Date(),
            };

            setMessages(prev => [...prev, userMessage]);
            setInput('');

            const endpoint = `${api}/${runPath}`;
            const requestBody = options?.body || {
                user_query: messageContent,
                request_id: `req-${Date.now()}`,
                session_id: currentSessionId,
            };

            try {
                const response = await interactiveServiceFetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    throw new Error(`Failed to create run: ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                const runId = result.run_id;

                setCurrentRunId(runId);
                setCurrentSessionId(result.session_id || currentSessionId);
            } catch (err) {
                handleError(err.message || 'An error occurred');
            }
        },
        [isLoading, api, runPath, currentSessionId, setCurrentSessionId, handleError]
    );

    const append = useCallback(
        async message => {
            await handleSubmit(null, { content: message.content });
        },
        [handleSubmit]
    );

    const reload = useCallback(async () => {
        const lastUserMessage = [...messages].reverse().find(m => m.role === MESSAGE_ROLES.USER);
        if (!lastUserMessage) return;

        const userMessageIndex = messages.findIndex(m => m.id === lastUserMessage.id);
        setMessages(messages.slice(0, userMessageIndex + 1));

        await handleSubmit(null, { content: lastUserMessage.content?.[0]?.data });
    }, [messages, handleSubmit]);

    const stop = useCallback(async () => {
        if (!currentRunId) return;
        setStopError(null);
        try {
            const assistantMessageId = streamingMessageId.current;
            const partialContent = assistantMessageId
                ? messages.find(m => m.id === assistantMessageId)?.content ?? []
                : [];
            await interactiveServiceFetch(`${api}/runs/${currentRunId}/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session_id: currentSessionId,
                    partial_content: partialContent,
                }),
            });
        } catch (err) {
            // Cancel API failed - backend is still running, keep streaming as normal
            setStopError('Failed to interrupt query');
        }
    }, [currentRunId, api, currentSessionId, messages]);

    const handleInputChange = useCallback(e => setInput(e.target.value), []);

    return {
        messages,
        input,
        setInput,
        handleSubmit,
        handleInputChange,
        isLoading,
        error,
        stopError,
        append,
        reload,
        stop,
        setMessages,
        sseConnected,
        // Session management
        currentSessionId,
        sessions,
        isLoadingSessions,
        loadingSessionId,
        clearCurrentSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
        fetchSessions,
    };
};

export default useChat;
